import React from 'react';
import { useProjectStore } from '../../../store/projectStore';
import { Link } from 'react-router-dom';
import { Badge } from '../../../components/Badge';
import { Button } from '../../../components/Button';
import { Card, CardContent } from '../../../components/Card';
import { LoadingTable } from '../../../components/Loading';
import toast from 'react-hot-toast';
import { Input } from '@/components/Input';

export function ProjectList() {
  const { projects, pagination, fetchProjects, isLoading, error } = useProjectStore();
  const [filters, setFilters] = React.useState({ page: 1, limit: 20, status: '', search: '' });

  React.useEffect(() => {
    fetchProjects(filters);
  }, [filters]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters({ ...filters, search: e.target.value, page: 1 });
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFilters({ ...filters, status: e.target.value, page: 1 });
  };

  if (isLoading && projects.length === 0) {
    return (
      <div>
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="tracking-tight text-foreground font-display text-page-title">Projects</h1>
            <p className="text-muted-foreground mt-1">Manage all your projects</p>
          </div>
          <Link to="/work/projects/new">
            <Button variant="primary">
              <span className="text-xl mr-2">+</span>
              New Project
            </Button>
          </Link>
        </div>
        <LoadingTable rows={5} columns={6} />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="tracking-tight text-foreground font-display text-page-title">Projects</h1>
          <p className="text-muted-foreground mt-1">Manage all your projects</p>
        </div>
        <Link to="/work/projects/new">
          <Button variant="primary">
            <span className="text-xl mr-2">+</span>
            New Project
          </Button>
        </Link>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
          <p className="text-destructive text-sm">{error}</p>
        </div>
      )}

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex gap-4">
            <Input
              type="text"
              placeholder="Search projects..."
              value={filters.search}
              onChange={handleSearch}
              className="flex-1 px-4 py-2 bg-card border border-border rounded-lg text-muted-foreground placeholder-text-40 focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <select
              value={filters.status}
              onChange={handleStatusChange}
              className="px-4 py-2 bg-card border border-border rounded-lg text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All Status</option>
              <option value="planning">Planning</option>
              <option value="active">Active</option>
              <option value="on-hold">On Hold</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>

            <select
              value={(filters as any).industry || ''}
              onChange={(e) => setFilters({ ...filters, [ 'industry' as any]: e.target.value, page: 1 } as any)}
              className="px-4 py-2 bg-card border border-border rounded-lg text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All Industries</option>
              <option value="it-services">IT Services</option>
              <option value="manufacturing">Manufacturing</option>
              <option value="retail">Retail</option>
              <option value="logistics">Logistics</option>
              <option value="ites">ITES/BPO</option>
              <option value="custom">Custom</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Projects Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-card">
                <tr>
                  <th className="text-left py-4 px-6 text-xs font-semibold text-muted-foreground">Industry / Code</th>
                  <th className="text-left py-4 px-6 text-xs font-semibold text-muted-foreground">Project Name</th>
                  <th className="text-left py-4 px-6 text-xs font-semibold text-muted-foreground">Status</th>
                  <th className="text-left py-4 px-6 text-xs font-semibold text-muted-foreground">Health</th>
                  <th className="text-left py-4 px-6 text-xs font-semibold text-muted-foreground">Progress</th>
                  <th className="text-right py-4 px-6 text-xs font-semibold text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-12">
                {projects.map((project) => (
                  <tr key={project.id} className="hover:bg-card/50 transition-colors group">
                    <td className="py-5 px-6">
                      <div className="flex flex-col">
                        <span className="text-xs text-muted-foreground font-semibold">{project.industryTemplate || 'General'}</span>
                        <span className="text-muted-foreground font-mono text-xs mt-0.5">{project.code}</span>
                      </div>
                    </td>
                    <td className="py-5 px-6">
                      <div className="flex flex-col">
                        <Link
                          to={`/work/projects/${project.id}`}
                          className="text-foreground group-hover:text-primary font-semibold transition-colors"
                        >
                          {project.name}
                        </Link>
                        <span className="text-xs text-muted-foreground mt-1 line-clamp-1">{project.description}</span>
                      </div>
                    </td>
                    <td className="py-5 px-6">
                      <Badge
                        variant={
                          project.status === 'active' ? 'success' :
                            project.status === 'completed' ? 'info' :
                              project.status === 'on-hold' ? 'warning' :
                                project.status === 'cancelled' ? 'error' : 'default'
                        }
                      >
                        {project.status}
                      </Badge>
                    </td>
                    <td className="py-5 px-6">
                      <div className="flex items-center gap-2">
                        <div className={`w-2.5 h-2.5 rounded-full ${project.healthStatus === 'green' ? 'bg-success shadow-[0_0_8px_rgba(34,197,94,0.4)]' :
                          project.healthStatus === 'yellow' ? 'bg-warning shadow-[0_0_8px_rgba(234,179,8,0.4)]' :
                            'bg-destructive shadow-[0_0_8px_rgba(239,68,68,0.4)]'
                          }`} />
                        <span className="text-xs font-semibold capitalize text-muted-foreground">{project.healthStatus}</span>
                      </div>
                    </td>
                    <td className="py-5 px-6">
                      <div className="flex flex-col gap-1.5 w-32">
                        <div className="flex justify-between text-xs font-semibold">
                          <span className="text-muted-foreground">PROGRESS</span>
                          <span className="text-foreground">{project.progress || 0}%</span>
                        </div>
                        <div className="h-1.5 w-full bg-card rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-1000 ${project.healthStatus === 'green' ? 'bg-success' :
                              project.healthStatus === 'yellow' ? 'bg-warning' : 'bg-destructive'
                              }`}
                            style={{ width: `${project.progress || 0}%` }}
                          />
                        </div>
                      </div>
                    </td>
                    <td className="py-5 px-6 text-right">
                      <Link
                        to={`/work/projects/${project.id}`}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-card border border-border text-muted-foreground hover:text-primary hover:border-primary/50 transition-all"
                      >
                        →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {projects.length === 0 && (
              <div className="text-center py-12">
                <p className="text-muted-foreground mb-4">No projects found</p>
                <Link to="/work/projects/new">
                  <Button variant="primary">Create Your First Project</Button>
                </Link>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex justify-between items-center mt-6">
          <p className="text-sm text-muted-foreground">
            Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
            {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
            {pagination.total} projects
          </p>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setFilters({ ...filters, page: pagination.page - 1 })}
              disabled={pagination.page === 1}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setFilters({ ...filters, page: pagination.page + 1 })}
              disabled={pagination.page === pagination.totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
